import { ComponentType, FunctionComponent, ReactNode, createContext, useContext, useMemo } from "react";
import type { ExchangeBetslipCapabilities, ExchangeBetslipContract } from "../../contract";
import { resolveExchangeCapabilities } from "../../capabilities/exchange-resolver";
import { useExchangeCapabilitiesSync } from "../../store/state/capabilities/useCapabilitiesSync";
import { useExchangeSettingsSync } from "../../store/state/exchange-settings/useExchangeSettingsSync";
import { useExchangePreferencesSync } from "../../store/state/preferences/usePreferencesSync";

export type ExchangeBetslipContext = Omit<ExchangeBetslipContract, "capabilities"> & {
  capabilities: ExchangeBetslipCapabilities;
};

const Context = createContext<ExchangeBetslipContext | null>(null);
Context.displayName = "ExchangeBetslipContext";

export function useExchangeBetslipContext(): ExchangeBetslipContext {
  const context = useContext(Context);
  if (context === null) {
    throw new Error("ExchangeBetslipContext consumer must be used within a ExchangeBetslipContextProvider");
  }
  return context;
}

/**
 * The API entrance for the exchange contract: hosts may supply a partial (or absent)
 * `capabilities` group here, and every consumer downstream still sees a complete
 * `ExchangeBetslipContext` because the gaps are resolved before reaching `Context.Provider`.
 */
export const ExchangeBetslipContextProvider: FunctionComponent<{
  children: ReactNode;
  value: ExchangeBetslipContract;
}> = ({ children, value }) => {
  const capabilities = useMemo(() => resolveExchangeCapabilities(value.capabilities), [value.capabilities]);
  const resolvedValue = useMemo<ExchangeBetslipContext>(() => ({ ...value, capabilities }), [value, capabilities]);

  useExchangeCapabilitiesSync(capabilities);
  useExchangeSettingsSync(value.discount, value.currencyDetails);
  useExchangePreferencesSync(value.preferences);

  return <Context.Provider value={resolvedValue}>{children}</Context.Provider>;
};

export function withExchangeBetslipContext<Selected extends object>(
  selector: (context: ExchangeBetslipContext) => Selected,
) {
  return function withExchangeBetslipContextSelector<P extends Selected>(
    Component: ComponentType<P>,
  ): FunctionComponent<Omit<P, keyof Selected>> {
    return function WithExchangeBetslipContext(props: Omit<P, keyof Selected>) {
      const selected = selector(useExchangeBetslipContext());
      return <Component {...(props as P)} {...selected} />;
    };
  };
}
